import { useEffect, useMemo, useRef, useState } from "react";
import { getAdminEmployeePerformanceOverview } from "../api/instateamReportsAdminApi";
import { AppIcon, StatusBanner } from "../components/InstateamMasterComponents";

const RANGE_OPTIONS = ["Today", "Week", "Month", "Year"];

function getMonthKey(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function getRangeKey(value) {
  return String(value || "month").toLowerCase();
}

function shortDate(value) {
  const date = value ? new Date(`${value}T00:00:00`) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short" }).format(date);
}

function formatMinutes(minutes) {
  const safe = Math.max(0, Math.round(Number(minutes || 0)));
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  return `${hours}h ${String(mins).padStart(2, "0")}m`;
}

function Sparkline({ values = [] }) {
  const safeValues = values.length ? values : [0, 0, 0];
  const max = Math.max(...safeValues, 1);
  const path = safeValues
    .map((value, index) => {
      const x = (index / Math.max(safeValues.length - 1, 1)) * 100;
      const y = 30 - (Number(value || 0) / max) * 22;
      return `${index ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <svg className="admin-employee-performance-sparkline" viewBox="0 0 100 34" aria-hidden="true">
      <path d={path} fill="none" stroke="#8b5cf6" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

export default function AdminEmployeePerformancePage({
  token,
  employee,
  initialRange = "Week",
  initialMonth = getMonthKey(),
  onBack,
}) {
  const [range, setRange] = useState(initialRange);
  const [month, setMonth] = useState(initialMonth || getMonthKey());
  const [overview, setOverview] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const monthRef = useRef(null);

  const employeeId = employee?.employeeObjectId || employee?._id;

  useEffect(() => {
    let isActive = true;

    async function fetchPerformance() {
      if (!token || !employeeId) return;
      try {
        setLoading(true);
        setMessage(null);
        const payload = await getAdminEmployeePerformanceOverview(token, employeeId, {
          range: getRangeKey(range),
          month,
        });
        if (isActive) setOverview(payload.overview || null);
      } catch (error) {
        if (isActive) setMessage({ type: "error", text: error.message });
      } finally {
        if (isActive) setLoading(false);
      }
    }

    fetchPerformance();
    return () => {
      isActive = false;
    };
  }, [employeeId, month, range, token]);

  const derived = useMemo(() => {
    const performance = overview?.performance || employee || {};
    const attendanceSummary = overview?.attendance?.summary || {};
    const taskMetrics = overview?.tasks?.metrics || {};
    const records = overview?.attendance?.records || [];
    const tasks = overview?.tasks?.tasks || [];
    const workedMinutes = records.reduce((sum, record) => sum + Number(record.workMinutes || 0), 0);
    const trendValues = records.slice(-8).map((record) => {
      if (record.status === "present") return 10;
      if (record.status === "late") return 7;
      if (record.status === "half_day") return 5;
      return 0;
    });

    return {
      employee: overview?.employee || employee || {},
      score: Number(performance.score || 0),
      scorePercent: Number(performance.scorePercent || 0),
      attendancePercent: Number(performance.attendancePercent || attendanceSummary.presentPercentage || 0),
      taskPercent: Number(performance.taskPercent || 0),
      presentDays: Number(performance.presentDays || attendanceSummary.present || 0),
      workingDays: Number(performance.attendanceRecords || attendanceSummary.workingDays || 0),
      completedTasks: Number(performance.completedTasks || taskMetrics.completed || 0),
      totalTasks: Number(performance.totalTasks || taskMetrics.total || tasks.length || 0),
      workedMinutes,
      trendValues,
    };
  }, [employee, overview]);

  return (
    <section className="admin-employee-performance-page">
      {message ? <StatusBanner type={message.type} text={message.text} onClose={() => setMessage(null)} /> : null}

      <div className="admin-employee-performance-toolbar">
        <button type="button" className="secondary-button" onClick={onBack}>
          <AppIcon name="chevronLeft" /> Back
        </button>
        <div>
          <h1>{derived.employee.fullName || "Employee Performance"}</h1>
          <p>{derived.employee.employeeId || ""} {derived.employee.designation ? `- ${derived.employee.designation}` : ""}</p>
        </div>
        <button
          type="button"
          className="admin-report-date"
          onClick={() => {
            if (monthRef.current?.showPicker) monthRef.current.showPicker();
            else monthRef.current?.click();
          }}
        >
          <AppIcon name="calendar" />
          {overview?.startKey ? `${shortDate(overview.startKey)} - ${shortDate(overview.endKey)}` : "Select month"}
          <input ref={monthRef} type="month" value={month} onChange={(event) => setMonth(event.target.value || getMonthKey())} />
          <AppIcon name="chevronDown" />
        </button>
        <div className="admin-report-range-tabs">
          {RANGE_OPTIONS.map((item) => (
            <button key={item} type="button" className={range === item ? "active" : ""} onClick={() => setRange(item)}>
              {item}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-employee-performance-grid">
        <article className="admin-employee-performance-score">
          <div className="admin-report-stat-icon purple"><AppIcon name="barChart" /></div>
          <div>
            <span>Overall Score</span>
            <strong>{derived.score.toFixed(1)} / 10</strong>
            <small>{derived.scorePercent.toFixed(1)}% live backend score</small>
          </div>
          <Sparkline values={derived.trendValues} />
        </article>
        <article><span>Attendance</span><strong>{derived.attendancePercent.toFixed(1)}%</strong><small>{derived.presentDays} / {derived.workingDays} days present</small></article>
        <article><span>Tasks</span><strong>{derived.taskPercent.toFixed(1)}%</strong><small>{derived.completedTasks} / {derived.totalTasks} completed</small></article>
        <article><span>Time Logged</span><strong>{formatMinutes(derived.workedMinutes)}</strong><small>from attendance records</small></article>
      </div>

      <div className="admin-employee-performance-detail-grid">
        <article className="admin-report-card">
          <h3>Attendance Records</h3>
          <div className="admin-employee-performance-list">
            {(overview?.attendance?.records || []).slice(-8).reverse().map((record) => (
              <div key={record._id || record.dateKey}>
                <strong>{shortDate(record.dateKey)}</strong>
                <span>{record.status}</span>
                <small>{formatMinutes(record.workMinutes)}</small>
              </div>
            ))}
            {overview?.attendance?.records?.length ? null : <p>No attendance data in this range.</p>}
          </div>
        </article>

        <article className="admin-report-card">
          <h3>Tasks</h3>
          <div className="admin-employee-performance-list">
            {(overview?.tasks?.tasks || []).slice(0, 8).map((task) => (
              <div key={task._id}>
                <strong>{task.title || "Assigned task"}</strong>
                <span>{task.status}</span>
                <small>{shortDate(task.dueDate)}</small>
              </div>
            ))}
            {overview?.tasks?.tasks?.length ? null : <p>No task data in this range.</p>}
          </div>
        </article>
      </div>

      {loading ? <div className="admin-report-loading">Refreshing employee performance...</div> : null}
    </section>
  );
}
